import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, CircularProgress, Container, TextField, Typography } from "@mui/material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import PatientFinancialTab from "../components/PatientFinancialTab";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, pageHeader } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { patientFinancialService, FinancialPatientOption } from "../services/patientFinancialService";
import { titleCaseName } from "../utils/nameCase";

/**
 * Financeiro do paciente (menu Financeiro): a MESMA tela de caixa da aba Financeiro do
 * cadastro (componente PatientFinancialTab), precedida da busca do paciente. Mexeu na aba,
 * mexeu aqui — é um componente só. Aceita ?patientId= para abrir direto num paciente.
 */
const PatientFinancialPage: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { getParam } = useRouter();

  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<FinancialPatientOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<FinancialPatientOption | null>(null);

  const initialPatientId = getParam("patientId") || "";

  useEffect(() => {
    document.title = "Clinic4Us - Financeiro do paciente";
  }, []);

  // Deep link: carrega o paciente da URL uma vez
  useEffect(() => {
    if (!initialPatientId) return;
    let cancelled = false;
    patientFinancialService.getPatient(initialPatientId)
      // O campo mostra o texto (inputValue controlado): sem isto o nome só aparecia depois de clicar
      .then((p) => { if (!cancelled) { setSelected(p); setSearch(titleCaseName(p.patientName)); } })
      .catch(() => { /* paciente inválido: fica na busca */ });
    return () => { cancelled = true; };
  }, [initialPatientId]);

  // Busca com atraso curto; mínimo 2 letras para não varrer a base a cada tecla
  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) { setOptions([]); return undefined; }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const result = await patientFinancialService.searchPatients(term);
        if (!cancelled) setOptions(result);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [search]);

  // Rótulo do campo = só o nome (em negrito quando selecionado); homônimos se distinguem na
  // lista pela 2ª linha: nascimento, 1º responsável e documento
  const optionLabel = useMemo(() => (p: FinancialPatientOption) => titleCaseName(p.patientName), []);
  const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  const ageOf = (iso: string) => {
    const birth = new Date(`${iso.slice(0, 10)}T00:00:00`);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age -= 1;
    return age;
  };
  // Identificação ao lado da busca (Hilton, 05/09): confirma que é o paciente certo antes de mexer no caixa
  const identityField = (label: string, value: string, grow = 1) => (
    <TextField size="small" label={label} value={value} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, flex: { xs: "1 1 100%", sm: `${grow} 1 ${grow > 1 ? 260 : 170}px` } }} />
  );
  const optionDetail = (p: FinancialPatientOption) => [
    p.patientBirthDate ? `Nasc. ${fmtDate(p.patientBirthDate)}` : null,
    p.responsibleName1 ? `Resp. ${titleCaseName(p.responsibleName1)}` : null,
    p.patientDocument ? p.patientDocument.replace(/[.\-\s/]/g, "") : null,
  ].filter(Boolean).join(" · ");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Financeiro do paciente</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Caixa do paciente: sessões em aberto, pagamento com recibo e estorno. A mesma tela da aba Financeiro do cadastro, aqui pela busca.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            {/* Busca separada do caixa por uma divisória: "quem" acima, "o quê" abaixo */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", pb: 2.5, mb: 2, borderBottom: `1px solid ${colors.borderLight}` }}>
              <Autocomplete
                sx={{ ...searchFieldSx, flex: { xs: "1 1 100%", sm: "2 1 320px" }, "& .MuiInputBase-input": { fontWeight: selected ? 700 : 400 } }}
                options={options}
                value={selected}
                onChange={(_, value) => { setSelected(value); if (!value) { setSearch(""); setOptions([]); } }}
                inputValue={search}
                onInputChange={(_, value, reason) => { if (reason !== "reset") setSearch(value); }}
                getOptionLabel={optionLabel}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                loading={searching}
                noOptionsText={search.trim().length < 2 ? "Digite ao menos 2 letras do nome ou documento" : "Nenhum paciente encontrado"}
                loadingText="Buscando…"
                size="small"
                renderOption={(props, p) => (
                  <li {...props} key={p.id}>
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{titleCaseName(p.patientName)}</Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary }}>{optionDetail(p) || "Sem dados de identificação"}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Paciente"
                    placeholder="Nome ou documento"
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    inputProps={{ ...params.inputProps, maxLength: 100 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {selected ? (
                <>
                  {identityField("Nascimento", selected.patientBirthDate ? `${fmtDate(selected.patientBirthDate)} (${ageOf(selected.patientBirthDate)} anos)` : "—")}
                  {identityField("Documento", selected.patientDocument || "—")}
                  {identityField("Responsável", titleCaseName(selected.responsibleName1 || selected.responsibleName2 || "") || "—", 2)}
                </>
              ) : null}
            </Box>

            {selected ? (
              <PatientFinancialTab key={selected.id} patientId={selected.id} />
            ) : (
              <Typography sx={{ textAlign: "center", color: colors.textSecondary, py: 6 }}>
                Busque e selecione um paciente para abrir o caixa.
              </Typography>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default PatientFinancialPage;
